import { useMemo, useState } from 'react';
import { Tag } from '@indipia/ui-core';
import { ThumbImg } from '../ThumbImg';
import { PhotoLightbox } from '../PhotoLightbox';
import type { WorkOrderListItem } from '../../types/worklog.types';
import { fmtTime, splitSentences } from './feedModel';

const ClockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

// ── 작업요청(WO) 카드 ────────────────────────────────────────
// 상세 페이지는 폐기(§5.6, 8dae370) — 상세 이동 없이 카드 안에서 "상세보기"로 본문만 펼친다(REQ-4-05).
export function WorkOrderCard({ item }: { item: WorkOrderListItem }) {
  const [lbIdx, setLbIdx] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  const MAX = 4;
  const shown = Math.min(item.photos.length, MAX);
  const overflow = item.photoCount > shown ? item.photoCount - (shown - 1) : 0;
  const thumbs = overflow > 0 ? item.photos.slice(0, shown - 1) : item.photos.slice(0, shown);
  const sentences = useMemo(() => splitSentences(item.orderContent), [item.orderContent]);
  const extraCount = sentences.length - 1;
  // 작업자(WORKER) 이름 — 실측 목업 헤더는 시간+이름+공종만(현장명·타입배지·참조인은 없음, REQ-4-01 UI 정합).
  const worker = item.recipients.find((r) => r.role === 'WORKER');

  return (
    <article className="h-full rounded-lg border border-border bg-surface-raised p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex shrink-0 items-center gap-1 text-caption text-text-muted">
          <ClockIcon />
          {fmtTime(item.sentAt)}
        </span>
        <span className="text-body-md font-semibold text-text">{worker?.partnerName ?? '-'}</span>
        <Tag className="!px-2 !py-[2px] !text-caption">{item.workTypeName}</Tag>
        {item.photoCount > 0 && <span className="text-caption text-text-muted">사진: {item.photoCount}장</span>}
      </div>

      {/* 본문 — 첫 문장 + "외 N건"(REQ-4-05), "상세보기"로 전체 펼침/접기 */}
      <p className="mt-2 text-body-md leading-relaxed text-text-secondary" style={{ whiteSpace: expanded ? 'pre-line' : undefined }}>
        {expanded ? item.orderContent : sentences[0]}
        {!expanded && extraCount > 0 && <span className="text-text-muted"> 외 {extraCount}건</span>}
      </p>
      {extraCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-caption font-bold text-info"
        >
          {expanded ? '접기' : '상세보기 ▾'}
        </button>
      )}

      {item.photoCount > 0 && (
        <div className="mt-2 flex gap-1.5">
          {thumbs.map((p, i) => (
            <button
              key={i}
              type="button"
              aria-label={`사진 ${i + 1} 크게보기`}
              onClick={(e) => { e.stopPropagation(); setLbIdx(i); }}
              className="h-14 w-14 overflow-hidden rounded-md border-[1.5px] p-0"
              style={{ borderColor: 'var(--gray-400)' }}
            >
              <ThumbImg url={p.thumbUrl} className="h-full w-full object-cover" />
            </button>
          ))}
          {overflow > 0 && (
            <button
              type="button"
              aria-label="사진 더 크게보기"
              onClick={(e) => { e.stopPropagation(); setLbIdx(shown - 1); }}
              className="flex h-14 w-14 items-center justify-center rounded-md border-[1.5px] bg-[var(--shell-skeleton)] text-body-md font-bold text-text-secondary"
              style={{ borderColor: 'var(--gray-400)' }}
            >
              +{overflow}
            </button>
          )}
        </div>
      )}

      {/* 라이트박스 오버레이 클릭이 카드 상세이동으로 버블링되지 않게 stopPropagation 래핑 */}
      <span onClick={(e) => e.stopPropagation()}>
        <PhotoLightbox
          photos={item.photos.map((p) => ({ url: p.url }))}
          index={lbIdx}
          onClose={() => setLbIdx(null)}
          onIndexChange={setLbIdx}
          renderImage={(photo, style) => <ThumbImg url={photo.url} alt="" imgStyle={style} />}
        />
      </span>
    </article>
  );
}
